import type { ConsoleEntry } from './types';

export function startConsoleCapture(
  callback: (entry: ConsoleEntry) => void
): () => void {
  const originalError = console.error;
  const originalWarn = console.warn;

  console.error = (...args: unknown[]) => {
    try {
      callback({
        level: 'error',
        message: args.map(stringifyArg).join(' '),
        timestamp: Date.now(),
      });
    } catch {
      // noop
    }
    originalError.apply(console, args);
  };

  console.warn = (...args: unknown[]) => {
    try {
      callback({
        level: 'warn',
        message: args.map(stringifyArg).join(' '),
        timestamp: Date.now(),
      });
    } catch {
      // noop
    }
    originalWarn.apply(console, args);
  };

  const onError = (event: ErrorEvent) => {
    try {
      callback({
        level: 'error',
        message: event.message || 'Uncaught error',
        source: event.filename
          ? `${event.filename}:${event.lineno}:${event.colno}`
          : undefined,
        stack: event.error instanceof Error ? event.error.stack : undefined,
        timestamp: Date.now(),
      });
    } catch {
      // noop
    }
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    try {
      const reason = event.reason;
      callback({
        level: 'error',
        message:
          reason instanceof Error
            ? reason.message
            : `Unhandled rejection: ${stringifyArg(reason)}`,
        stack: reason instanceof Error ? reason.stack : undefined,
        timestamp: Date.now(),
      });
    } catch {
      // noop
    }
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    console.error = originalError;
    console.warn = originalWarn;
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}

function stringifyArg(arg: unknown): string {
  if (typeof arg === 'string') return arg;
  if (arg instanceof Error) return arg.message;
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
}
